<!-- TODO: Add loading skeleton -->
<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.setting', 2), to: '/settings' },
      { label: $t('ui.entities.departments.callCenter', 2), to: '/settings/call-centers' },
      { label: callCenter.basicDetails.name ? callCenter.basicDetails.name : 'Unknown Call Center' }
    ]"
  >
    <template #right>
      <Dropdown
        v-if="currentTab == 'dealerships'"
        @close="
          (() => ((dealershipToAdd.dealershipId = null), (dealershipToAdd.phoneNumberId = null)),
          (dealershipToAdd.smsNumberId = null),
          (dealershipToAdd.emailAccountId = null))
        "
      >
        <template #triggerContent>
          <Button
            label="Add Dealership"
            outlined
          />
        </template>
        <div class="px-6 py-4 max-w-96">
          <FormGroup>
            <Select
              v-model="dealershipToAdd.dealershipId"
              searchable
              value-key="id"
              icon="car-building"
              label-key="name"
              placeholder="Select dealership"
              class="min-w-72"
              :options="dealershipsList"
              :has-error="vAddDealership$.dealershipId.$error"
            >
              <template #option="{ option }">
                <div class="flex gap-2 items-center">
                  <div>{{ option.name }}</div>
                </div>
              </template>
            </Select>
            <InputError :has-error="vAddDealership$.dealershipId.$error">
              {{ vAddDealership$.dealershipId.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup>
            <Select
              v-model="dealershipToAdd.phoneNumberId"
              :options="callCenterPhoneNumbers.phone"
              label-key="name"
              searchable
              value-key="id"
              icon="phone"
              placeholder="Select phone number"
            >
              <template #option="{ option }">
                <div class="items-center">
                  <div>{{ option.name }}</div>
                  <div class="text-tertiary text-sm">{{ option.numberIso }}</div>
                </div>
              </template>
            </Select>
          </FormGroup>
          <FormGroup>
            <Select
              v-model="dealershipToAdd.smsNumberId"
              :options="callCenterPhoneNumbers.sms"
              label-key="name"
              searchable
              value-key="id"
              class="min-w-72"
              placeholder="Select SMS number"
              icon="sms"
            >
              <template #option="{ option }">
                <div class="items-center">
                  <div>{{ option.name }}</div>
                  <div class="text-tertiary text-sm">{{ option.numberIso }}</div>
                </div>
              </template>
            </Select>
          </FormGroup>
          <FormGroup>
            <Select
              v-model="dealershipToAdd.emailAccountId"
              :options="emailAccounts"
              label-key="emailAddress"
              value-key="id"
              icon="at"
              searchable
              class="min-w-72"
              placeholder="Select email address"
            >
              <template #option="{ option }">
                <div class="items-center">
                  <div>{{ option.name }}</div>
                  <div class="text-tertiary text-sm">{{ option.emailAddress }}</div>
                </div>
              </template>
            </Select>
          </FormGroup>
          <Button
            label="Add"
            class="mb-2"
            block
            :is-loading="addDealershipRequest.isLoading.value"
            @click="addDealership()"
          />
        </div>
      </Dropdown>

      <Dropdown @close="() => ((userToAdd.userId = null), (userToAdd.role = null))" v-else-if="currentTab == 'users'">
        <template #triggerContent>
          <Button
            label="Add User"
            outlined
          />
        </template>
        <div class="px-6 py-4 max-w-96">
          <FormGroup>
            <Select
              v-model="userToAdd.userId"
              searchable
              placeholder="Select user"
              :has-error="vAddUser$.userId.$error"
              :options="availableUsersList"
            >
              <template #option="{ option }">
                <div class="flex gap-2 items-center">
                  <Avatar
                    :url="option.profilePicUrl"
                    size="xs"
                    :text="option.initials"
                  />
                  <div>{{ option.name }}</div>
                </div>
              </template>
            </Select>
            <InputError :has-error="vAddUser$.userId.$error">
              {{ vAddUser$.userId.$errors[0]?.$message }}
            </InputError>
          </FormGroup>
          <FormGroup>
            <Select
              v-model="userToAdd.role"
              :options="[
                { value: 'agent', label: 'Agent' },
                { value: 'manager', label: 'Manager' }
              ]"
              placeholder="Select role"
              :has-error="vAddUser$.role.$error"
            />
            <InputError :has-error="vAddUser$.role.$error">{{ vAddUser$.role.$errors[0]?.$message }}</InputError>
          </FormGroup>
          <Button
            label="Add"
            class="mb-2"
            block
            :is-loading="addUserRequest.isLoading.value"
            @click="addUser()"
          />
        </div>
      </Dropdown>
    </template>
  </PageHeader>

  <div>
    <Tabs
      v-model="currentTab"
      :tabs="tabs"
      type="pills-rail"
      class="mb-8 item"
    />
  </div>

  <div
    v-if="!callCenter.basicDetails.id"
    class="w-full"
  >
    <Skeleton
      height="16rem"
      width="100%"
    />
  </div>

  <div v-else>
    <div class="md:col-span-3">
      <div v-if="currentTab == 'basic'">
        <Card>
          <CardBody>
            <CardTitle
              title="Basic Info"
              :has-margin="true"
            />

            <div class="grid md:grid-cols-2 gap-x-8">
              <FormGroup
                :label="$t('ui.common.name')"
                :is-required="v$.basicDetails.name.required"
              >
                <Input
                  v-model="callCenter.basicDetails.name"
                  :has-error="v$.basicDetails.name.$error"
                />
                <InputError :has-error="v$.basicDetails.name.$error">
                  {{ v$.basicDetails.name.$errors[0]?.$message }}
                </InputError>
              </FormGroup>
            </div>
            <Button
              label="Save Changes"
              type="primary"
              class="mt-4"
              :is-loading="formState.basicDetailsSubmitting"
              @click="submitBasicForm"
            />
          </CardBody>
        </Card>
      </div>

      <div v-if="currentTab == 'dealerships'">
        <Card>
          <DataTable
            v-model:filters="dealershipFilters"
            :value="callCenter.dealerships"
            data-key="dealershipId"
            removable-sort
            sort-field="dealershipId"
            :sort-order="-1"
            responsive-layout="scroll"
            scrollable
            filter-display="menu"
            state-key="dt-settings-call-center-dealerships"
            state-storage="session"
            :global-filter-fields="[
              'dealershipId',
              'dealership.id',
              'dealership.name',
              'phoneNumber.numberIso',
              'smsNumber.numberIso',
              'emailAddress.emailAddress'
            ]"
          >
            <template #empty>
              <div>No records found</div>
            </template>
            <template #header>
              <div class="flex justify-between">
                <span class="flex justify-between gap-4">
                  <span>
                    <Input
                      v-model="dealershipFilters['global'].value"
                      :placeholder="$t('ui.common.search', 1)"
                      icon="search"
                    />
                  </span>
                  <Button
                    :label="$t('ui.actions.clear', 1)"
                    severity="secondary"
                    type="button"
                    outlined
                    icon="filter-slash"
                    @click="clearDealershipFilters()"
                  />
                </span>
              </div>
            </template>
            <Column
              field="dealershipId"
              header="ID"
              :sortable="true"
            >
              <template #body="{ data }">
                {{ data.dealershipId }}
              </template>
            </Column>
            <Column
              field="dealership.name"
              header="Dealership"
              :sortable="true"
            >
              <template #body="{ data }">
                <div class="flex items-center gap-3">
                  <Avatar
                    :url="data.dealership.manufacturerLogoUrl"
                    is-logo
                    size="md"
                  />
                  <div>
                    <router-link
                      :to="'/settings/dealerships/' + data.dealership.id"
                      class="font-medium link-hover"
                    >
                      {{ data.dealership.name }}
                    </router-link>
                  </div>
                </div>
              </template>
            </Column>
            <Column
              header="Phone Number"
              style="min-width: 14rem"
            >
              <template #body="{ data }">
                <Select
                  v-model="data.phoneNumber"
                  :options="callCenterPhoneNumbers.phone"
                  label-key="numberIso"
                  searchable
                  value-key="numberIso"
                  on-select-return-as-object
                  icon="phone"
                  @select="val => updateDealershipPhoneNumber(val, data.dealershipId)"
                >
                  <template #option="{ option }">
                    <div class="items-center">
                      <div>{{ option.name }}</div>
                      <div class="text-tertiary text-sm">{{ option.numberIso }}</div>
                    </div>
                  </template>
                </Select>
              </template>
            </Column>
            <Column
              header="SMS Number"
              style="min-width: 14rem"
            >
              <template #body="{ data }">
                <Select
                  v-model="data.smsNumber"
                  :options="callCenterPhoneNumbers.sms"
                  label-key="numberIso"
                  value-key="numberIso"
                  searchable
                  on-select-return-as-object
                  icon="sms"
                  class="min-w-40"
                  @select="val => updateDealershipSmsNumber(val, data.dealershipId)"
                >
                  <template #option="{ option }">
                    <div class="items-center">
                      <div>{{ option.name }}</div>
                      <div class="text-tertiary text-sm">{{ option.numberIso }}</div>
                    </div>
                  </template>
                </Select>
              </template>
            </Column>
            <Column
              header="Email Address"
              style="min-width: 18rem"
            >
              <template #body="{ data }">
                <Select
                  v-model="data.emailAddress"
                  :options="emailAccounts"
                  label-key="emailAddress"
                  value-key="emailAddress"
                  searchable
                  on-select-return-as-object
                  icon="at"
                  class="min-w-40"
                  @select="val => updateDealershipEmailAddress(val, data.dealershipId)"
                >
                  <template #option="{ option }">
                    <div class="items-center">
                      <div>{{ option.name }}</div>
                      <div class="text-tertiary text-sm">{{ option.emailAddress }}</div>
                    </div>
                  </template>
                </Select>
              </template>
            </Column>
            <Column
              field="isPrimary"
              :sortable="true"
            >
              <template #header>
                <div
                  v-tippy="`The dealership will use this call centre's details to contact customers`"
                  class="whitespace-nowrap"
                >
                  Is Primary
                </div>
              </template>
              <template #body="{ data }">
                <ToggleSwitch
                  v-model="data.isPrimary"
                  @change="val => updateDealershipIsPrimary(val, data.dealershipId)"
                />
              </template>
            </Column>
            <Column
              v-if="$can('manage_communication_settings')"
              header=""
            >
              <template #body="{ data }">
                <Button
                  v-tippy="'Remove from call centre'"
                  icon="trash"
                  severity="danger"
                  :is-loading="
                    removeDealershipRequest.isLoading.value && dealershipToRemove == data.dealership.id
                  "
                  plain
                  @click="removeDealership(data)"
                />
              </template>
            </Column>
          </DataTable>
        </Card>
      </div>

      <div v-if="currentTab == 'users'">
        <Card>
          <DataTable
            v-model:filters="userFilters"
            :value="callCenter.users"
            data-key="id"
            removable-sort
            sort-field="id"
            :sort-order="-1"
            responsive-layout="scroll"
            scrollable
            filter-display="menu"
            state-key="dt-settings-call-center-users"
            state-storage="session"
            :global-filter-fields="['id', 'name', 'jobTitle', 'role']"
          >
            <template #empty>
              <div>No records found</div>
            </template>
            <template #header>
              <div class="flex justify-between">
                <span class="flex justify-between gap-4">
                  <span>
                    <Input
                      v-model="userFilters['global'].value"
                      :placeholder="$t('ui.common.search', 1)"
                      icon="search"
                    />
                  </span>
                  <Button
                    :label="$t('ui.actions.clear', 1)"
                    severity="secondary"
                    type="button"
                    outlined
                    icon="filter-slash"
                    @click="clearUserFilters()"
                  />
                </span>
              </div>
            </template>
            <Column
              field="id"
              header="ID"
              :sortable="true"
            >
              <template #body="{ data }">
                {{ data.id }}
              </template>
            </Column>
            <Column
              field="name"
              header="Name"
              :sortable="true"
            >
              <template #body="{ data }">
                <div class="flex items-center gap-3">
                  <Avatar
                    :url="data.profilePicUrl"
                    size="md"
                    :text="data.initials"
                  />
                  <div>
                    <router-link
                      :to="'/settings/users/' + data.id"
                      class="font-medium link-hover"
                    >
                      {{ data.name }}
                    </router-link>
                    <div class="text-sm">{{ data.jobTitle }}</div>
                  </div>
                </div>
              </template>
            </Column>
            <Column
              field="role"
              header="Role"
              :sortable="true"
              style="min-width: 13rem"
            >
              <template #body="{ data }">
                <Select
                  v-model="data.role"
                  :options="[
                    { value: 'agent', label: 'Agent' },
                    { value: 'manager', label: 'Manager' }
                  ]"
                  class="max-w-52"
                  @select="val => updateUserRole(val, data.id)"
                />
              </template>
            </Column>
            <Column
              v-if="$can('manage_call_center_team')"
              header=""
            >
              <template #body="{ data }">
                <Button
                  v-tippy="'Remove from call centre'"
                  icon="trash"
                  severity="danger"
                  :is-loading="removeUserRequest.isLoading.value && userToRemove == data.id"
                  plain
                  @click="removeUser(data)"
                />
              </template>
            </Column>
          </DataTable>
        </Card>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import useApiRequest from '@/composables/useApiRequest'
import { alertToast } from '@/utilities/notification'
import { useAuthStore } from '@/stores/AuthStore'
import { useAccessStore } from '@/stores/AccessStore'

import PageHeader from '@/components/page/PageHeader.vue'
import Card from '@/components/card/Card.vue'
import CardBody from '@/components/card/CardBody.vue'
import CardTitle from '@/components/card/CardTitle.vue'
import FormGroup from '@/components/forms/FormGroup.vue'
import Tabs from '@/components/tabs/Tabs.vue'
import Input from '@/components/forms/Input.vue'
import InputError from '@/components/forms/InputError.vue'
import Avatar from '@/components/avatar/Avatar.vue'
import Button from '@/components/button/Button.vue'
import Select from '@/components/forms/Select.vue'
import Dropdown from '@/components/dropdown/Dropdown.vue'
import Skeleton from '@/components/skeleton/Skeleton.vue'
import ToggleSwitch from '@/components/forms/ToggleSwitch.vue'
import { FilterMatchMode } from '@primevue/core/api'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const accessStore = useAccessStore()

const callCenterRequest = useApiRequest()
const callCenterSubmitFormRequest = useApiRequest()

const addUserRequest = useApiRequest()
const addDealershipRequest = useApiRequest()
const removeDealershipRequest = useApiRequest()
const removeUserRequest = useApiRequest()
const emailAccountsRequest = useApiRequest()
const dealershipPhoneNumberRequest = useApiRequest()
const dealershipSmsNumberRequest = useApiRequest()
const dealershipEmailRequest = useApiRequest()

const emailAccounts = ref([])

const callCenter = ref({
  basicDetails: {
    id: null,
    name: null
  },
  dealerships: [],
  users: [],
  phoneNumbers: []
})

const userToAdd = ref({
  userId: null,
  role: 'agent'
})

const userInputRules = {
  userId: { required },
  role: { required }
}

const dealershipToAdd = ref({
  dealershipId: null,
  phoneNumberId: null,
  emailAccountId: null,
  smsNumberId: null
})

const dealershipInputRules = {
  dealershipId: { required },
  phoneNumberId: {},
  emailAccountId: {},
  smsNumberId: {}
}
const vAddDealership$ = useVuelidate(dealershipInputRules, dealershipToAdd)
const vAddUser$ = useVuelidate(userInputRules, userToAdd)

function fetchCallCenter() {
  try {
    callCenterRequest.send({ endpoint: '/v1/call-centers/' + route.params.id, method: 'GET' }).then(response => {
      if (response.success) {
        callCenter.value.basicDetails = {
          id: response.data.id,
          name: response.data.name
        }
        callCenter.value.dealerships = response.data.dealerships
        callCenter.value.users = response.data.users
        callCenter.value.phoneNumbers = response.data.phoneNumbers
      } else {
        alertToast('Failed to fetch data', response.message, 'error')
      }
    })
  } catch (err) {
    alertToast('Failed to fetch data', err.message, 'error')
  }
}
fetchCallCenter()

try {
  emailAccountsRequest
    .send({ endpoint: `/v1/comms/email-accounts?callCenterId=${route.params.id}`, method: 'GET' })
    .then(response => {
      if (response.success) {
        emailAccounts.value = response.data
      } else {
        alertToast('Failed to fetch email accounts', response.message, 'error')
      }
    })
} catch (err) {
  alertToast('Failed to fetch email accounts', err.message, 'error')
}

const callCenterPhoneNumbers = computed(() => {
  let phone = []
  let sms = []

  if (callCenter.value.phoneNumbers) {
    phone = callCenter.value.phoneNumbers.filter(n => n.canMakeCalls)
    sms = callCenter.value.phoneNumbers.filter(n => n.canSendSms)
  }
  return { phone, sms }
})

const dealershipFilters = ref([])
const userFilters = ref([])

function initDealershipFilters() {
  dealershipFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }
}
initDealershipFilters()

function initUserFilters() {
  userFilters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }
}
initUserFilters()

function clearDealershipFilters() {
  sessionStorage.removeItem('dt-settings-call-center-dealerships')
  initDealershipFilters()
}

function clearUserFilters() {
  sessionStorage.removeItem('dt-settings-call-center-users')
  initUserFilters()
}

function updateDealershipPhoneNumber(val, id) {
  const dataToPost = { phoneNumberId: val.id }
  try {
    dealershipPhoneNumberRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/call-centers/${callCenter.value.basicDetails.id}/dealerships/${id}`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (response.success) {
          alertToast('Updated number', null, 'success')
        } else {
          alertToast('Failed to update dealership number', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to update dealership number', err.message, 'error')
  }
}

function updateDealershipSmsNumber(val, id) {
  const dataToPost = { smsNumberId: val.id }
  try {
    dealershipSmsNumberRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/call-centers/${callCenter.value.basicDetails.id}/dealerships/${id}`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (response.success) {
          alertToast('Updated number', null, 'success')
        } else {
          alertToast('Failed to update dealership number', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to update dealership number', err.message, 'error')
  }
}

function updateDealershipEmailAddress(val, id) {
  const dataToPost = { emailAccountId: val.id }
  try {
    dealershipEmailRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/call-centers/${callCenter.value.basicDetails.id}/dealerships/${id}`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (response.success) {
          alertToast('Updated email address', null, 'success')
        } else {
          alertToast('Failed to update dealership email', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to update dealership email', err.message, 'error')
  }
}

function updateDealershipIsPrimary(val, id) {
  const dataToPost = { isPrimary: val }

  try {
    dealershipEmailRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/call-centers/${callCenter.value.basicDetails.id}/dealerships/${id}`,
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (response.success) {
          alertToast('Updated is primary', null, 'success')
        } else {
          alertToast('Failed to update is primary', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to update is primary', err.message, 'error')
  }
}

const users = ref(null)
const usersApiRequest = useApiRequest()
function fetchAvailableUsers() {
  const urlParams = {
    userType: authStore.details.userType == 1 ? 'all' : 'externalUsers',
    dealershipsAllowed: accessStore.accessAllowed.dealerships.map(d => {
      return d.id
    }),
    level: authStore.details.userType == 1 ? null : authStore.details.level
  }

  try {
    usersApiRequest.send({ endpoint: `/v1/users`, method: 'GET', params: urlParams }).then(response => {
      if (response.success) {
        users.value = response.data
      } else {
        alertToast('Failed to fetch users', response.message, 'error')
      }
    })
  } catch (err) {
    alertToast('Failed to fetch users', err.message, 'error')
  }
}

fetchAvailableUsers()

const availableUsersList = computed(() => {
  // if no users, return empty array
  if (!users.value) return []

  const callCenterUsers = callCenter.value.users.map(u => {
    return u.id
  })

  // return users that aren't already in the call center
  const usersFiltered = users.value.filter(u => !callCenterUsers.includes(u.id))
  return usersFiltered.map(u => {
    return {
      id: u.id,
      value: u.id,
      label: u.fullName,
      role: null,
      name: u.fullName,
      initials: u.initials,
      jobTitle: u.jobTitle,
      profilePicUrl: u.profilePicUrl
    }
  })
})

const dealerships = ref(accessStore.accessAllowed.dealerships)
const dealershipsList = computed(() => {
  // if no users, return empty array
  if (!dealerships.value) return []

  const callCenterDealerships = callCenter.value.dealerships.map(u => {
    return u.dealershipId
  })

  // return users that aren't already in the call center
  const dealershipsFiltered = dealerships.value.filter(d => !callCenterDealerships.includes(d.id))
  return dealershipsFiltered
})

const updateUserRoleRequest = useApiRequest()
function updateUserRole(val, id) {
  try {
    updateUserRoleRequest
      .send({
        method: 'PATCH',
        endpoint: `/v1/call-centers/${route.params.id}/users/${id}`,
        data: { role: val }
      })
      .then(response => {
        if (response.success) {
          alertToast('Updated role', null, 'success')
        } else {
          alertToast('Failed to update role', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to update role', err.message, 'error')
  }
}

async function addUser() {
  const isFormCorrect = await vAddUser$.value.$validate()
  if (!isFormCorrect) {
    return
  }

  try {
    addUserRequest
      .send({
        method: 'POST',
        endpoint: `/v1/call-centers/${route.params.id}/users`,
        data: JSON.stringify(userToAdd.value)
      })
      .then(response => {
        if (response.success) {
          const userObj = availableUsersList.value.find(u => u.id == userToAdd.value.userId)
          userObj.role = userToAdd.value.role
          callCenter.value.users.push(userObj)
          alertToast('User added', response.message, 'success')
        } else {
          alertToast('Failed to add user', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to add user', err.message, 'error')
  }
}

async function addDealership() {
  const isFormCorrect = await vAddDealership$.value.$validate()
  if (!isFormCorrect) {
    return
  }

  try {
    addDealershipRequest
      .send({
        method: 'POST',
        endpoint: `/v1/call-centers/${route.params.id}/dealerships`,
        data: JSON.stringify(dealershipToAdd.value)
      })
      .then(response => {
        if (response.success) {
          fetchCallCenter()
          // TODO: manually add the dealership object to the list instead of fetching data again

          alertToast('Dealership added', response.message, 'success')
        } else {
          alertToast('Failed to add dealership', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to add dealership', err.message, 'error')
  }
}

const userToRemove = ref(null)
function removeUser(user) {
  userToRemove.value = user.id

  try {
    removeUserRequest
      .send({ method: 'DELETE', endpoint: `/v1/call-centers/${route.params.id}/users/${user.id}` })
      .then(response => {
        if (response.success) {
          alertToast('User removed', null, 'success')
          const index = callCenter.value.users.indexOf(user)
          if (index > -1) {
            callCenter.value.users.splice(index, 1)
          }
        } else {
          alertToast('Failed to remove user', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to remove user', err.message, 'error')
  }
}

const dealershipToRemove = ref(null)
function removeDealership(dealership) {
  dealershipToRemove.value = dealership.dealership.id

  try {
    removeDealershipRequest
      .send({
        method: 'DELETE',
        endpoint: `/v1/call-centers/${route.params.id}/dealerships/${dealership.dealershipId}`
      })
      .then(response => {
        if (response.success) {
          alertToast('Dealership removed', null, 'success')
          const index = callCenter.value.dealerships.indexOf(dealership)
          if (index > -1) {
            callCenter.value.dealerships.splice(index, 1)
          }
        } else {
          alertToast('Failed to remove dealership', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to remove dealership', err.message, 'error')
  }
}

const currentTab = ref(route.params.tab)
const tabs = ref([
  {
    id: 'basic',
    label: t('ui.common.basicInfo'),
    // icon: 'phone-office',
    to: '/settings/call-centers/' + route.params.id
  },
  {
    id: 'dealerships',
    label: t('ui.entities.dealership', 2),
    // icon: 'car-building',
    to: '/settings/call-centers/' + route.params.id + '/dealerships'
  },
  {
    id: 'users',
    label: t('ui.common.user', 2),
    // icon: 'users',
    to: '/settings/call-centers/' + route.params.id + '/users'
  }
])

// Set tab to 'Basic' if no tab is set
if (route.params.tab === '') {
  currentTab.value = 'basic'
} else {
  currentTab.value = route.params.tab
}

// If the user changes the tab, update the route
watch(
  () => currentTab.value,
  newTab => {
    currentTab.value = newTab
    // fetch tab route
    const tab = tabs.value.find(tab => tab.id === newTab)
    if (tab) {
      router.replace(tab.to)
    }
  }
)

const basicDetailsRules = {
  basicDetails: {
    id: { required },
    name: { required }
  },
  dealerships: {},
  users: {},
  phoneNumbers: {}
}

const v$ = useVuelidate(basicDetailsRules, callCenter)

const formState = ref({
  basicDetailsSubmitting: false,
  dealershipsDetailsSubmitting: false,
  usersDetailsSubmitting: false,
  phoneNumbersDetailsSubmitting: false
})

async function submitBasicForm() {
  const isFormCorrect = await v$.value.$validate()

  if (isFormCorrect) {
    formState.value.basicDetailsSubmitting = true

    const dataToPost = {
      name: callCenter.value.basicDetails.name,
      dealerships: callCenter.value.dealerships,
      users: callCenter.value.users,
      phoneNumbers: callCenter.value.phoneNumbers
    }

    callCenterSubmitFormRequest
      .send({
        endpoint: '/v1/call-centers/' + route.params.id,
        method: 'PATCH',
        data: JSON.stringify(dataToPost)
      })
      .then(response => {
        if (response.success) {
          alertToast('Updated', null, 'success')
        } else {
          alertToast('Failed to update basic details', response.message, 'error')
        }
        formState.value.basicDetailsSubmitting = false
      })
  }
}
</script>
